import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { applyLicenseKey } from "../../../../utils/License";
import { getAllCallLogs } from "./ShowAllService";
import { CallFilters, callFiltersToParams } from "../../CallFilterBar";
import RecordingCell from "./RecordingCell";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import YhSelect from "../../../../ui/YhSelect";
import urls from "../../../../global/constants/UrlConstants";
import {
  formatCallDuration,
  statusBadgeClass,
} from "../../callLogHelpers";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../../../Shared/Phone/PhoneWithCountryCode";
import { selectActiveOrganizationId } from "../../../../redux/authSlice";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const Empty = () => <span className="text-[#9CA3AF]">—</span>;

const STATUS_FILTERS = [
  { label: "All calls", value: "all" },
  { label: "Answered", value: "answered" },
  { label: "Missed / failed", value: "missed" },
  { label: "Connecting", value: "initiated" },
  { label: "With recording", value: "recording" },
];

type Stats = {
  total: number;
  answered: number;
  missed: number;
  initiated: number;
  withRecording: number;
};

const emptyStats = (): Stats => ({
  total: 0,
  answered: 0,
  missed: 0,
  initiated: 0,
  withRecording: 0,
});

const StatCard = ({
  label,
  value,
  active,
  onClick,
  accent,
}: {
  label: string;
  value: number;
  active?: boolean;
  onClick?: () => void;
  accent: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-2xl border px-4 py-3 text-left transition ${
      active
        ? "border-[#841C39] bg-[#FFF8FA] shadow-sm"
        : "border-[#EEF0F5] bg-white hover:border-[#F3D4DE]"
    }`}
  >
    <p className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
      {label}
    </p>
    <p className={`mt-1 text-2xl font-bold tabular-nums ${accent}`}>
      {Number(value || 0).toLocaleString("en-IN")}
    </p>
  </button>
);

const ShowAll = ({ filters }: { filters: CallFilters }) => {
  const history = useHistory();
  const location = useLocation();
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const dashboardQuery = new URLSearchParams(location.search);
  const fromDashboard = dashboardQuery.get("dashboard") === "1";
  const focusCallId = dashboardQuery.get("focus") || "";
  const filterQuery = callFiltersToParams(filters);

  useEffect(() => {
    applyLicenseKey();
  }, []);

  const [callData, setCallData] = useState<any[]>([]);
  const [metaData, setMetaData] = useState<any>({});
  const [stats, setStats] = useState<Stats>(emptyStats());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });

  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterQuery.period,
    filterQuery.from,
    filterQuery.to,
    filters.teamIds,
    filters.agentIds,
    filters.projectIds,
    filters.sources,
    filters.stages,
  ]);

  useEffect(() => {
    tableDataShowHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationModel,
    search,
    statusFilter,
    activeOrganizationId,
    location.search,
    filterQuery.period,
    filterQuery.from,
    filterQuery.to,
    filters.teamIds,
    filters.agentIds,
    filters.projectIds,
    filters.sources,
    filters.stages,
  ]);

  const openLead = (row: any) => {
    const leadId = row?.leadId || row?.lead_id;
    if (!leadId) return;
    history.push(urls.ALLLEADDETAILS.replace(":id", String(leadId)), {
      lead: {
        _id: leadId,
        leadName: row?.leadName,
        leadPhone: row?.callerNumber,
      },
      type: "",
    });
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Lead / Number",
      field: "leadName",
      minWidth: 150,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const leadId = params.row?.leadId || params.row?.lead_id;
        const name = params.row?.leadName || params.row?.callerNumber || null;
        const matchStatus = String(params.row?.inboundMatchStatus || "");
        const matches = Array.isArray(params.row?.leadMatches)
          ? params.row.leadMatches
          : [];
        const preferred =
          params.row?.preferredAssigneeName ||
          matches[0]?.assignedUserName ||
          "";
        const isUnknown =
          matchStatus === "unknown" || name === "Unknown number";
        const phoneRaw =
          params.row?.callerNumberWithCode ||
          params.row?.callerNumber ||
          params.row?.uid ||
          "";
        const phone = formatPhoneKeepingCountryCode(
          resolveLeadPhoneForDisplay({
            leadPhone: phoneRaw,
            leadPhoneMasked: params.row?.leadPhoneMasked || phoneRaw,
            phoneMasked: params.row?.phoneMasked,
            canViewFullPhone: params.row?.canViewFullPhone,
          })
        );
        return (
          <div className="flex flex-col items-start gap-0.5">
            {leadId && name && !isUnknown ? (
              <button
                type="button"
                onClick={() => openLead(params.row)}
                className="text-left text-sm font-semibold text-[#841C39] hover:underline"
                title="Open lead details"
              >
                {name}
              </button>
            ) : (
              <span className="text-sm font-medium text-[#111827]">
                {name || <Empty />}
              </span>
            )}
            {isUnknown ? (
              <button
                type="button"
                onClick={() =>
                  history.push(urls.ADDLEADPAGE, {
                    prefillPhone: phoneRaw,
                    fromCallLog: params.row?._id,
                  })
                }
                className="text-[11px] font-semibold text-[#841C39] hover:underline"
              >
                Create lead
              </button>
            ) : null}
            {matchStatus === "multiple" && matches.length > 1 ? (
              <span className="text-[10px] text-[#6B7280]">
                {matches.length} matches
                {preferred ? ` · prefer ${preferred}` : ""}
              </span>
            ) : preferred && !isUnknown ? (
              <span className="text-[10px] text-[#6B7280]">
                Assigned: {preferred}
              </span>
            ) : null}
            {leadId && phone && !isUnknown ? (
              <button
                type="button"
                onClick={() => openLead(params.row)}
                className="text-left text-xs text-[#6B7280] hover:text-[#841C39] hover:underline"
                title="Open lead details"
              >
                {phone}
              </button>
            ) : (
              <span className="text-xs text-[#6B7280]">{phone}</span>
            )}
          </div>
        );
      },
    },
    {
      headerName: "Project",
      field: "project_name",
      minWidth: 160,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-sm font-medium text-[#111827]">
            {params.row.developer_name || <Empty />}
          </span>
          <span className="text-xs text-[#6B7280]">
            {params.row?.project_name || <Empty />}
          </span>
        </div>
      ),
    },
    {
      headerName: "Call By",
      field: "agentName",
      minWidth: 120,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.agentName || <Empty />}
        </span>
      ),
    },
    {
      headerName: "Duration",
      field: "durationFormatted",
      minWidth: 100,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const label = formatCallDuration(params.row);
        return label ? (
          <span className="text-sm tabular-nums text-[#374151]">{label}</span>
        ) : (
          <Empty />
        );
      },
    },
    {
      headerName: "Date & Time",
      field: "callStartTime",
      minWidth: 130,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        if (!params.row?.callStartTime) return <Empty />;
        const formattedDate = new Date(params.row.callStartTime)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-");
        const formattedTime = new Date(
          params.row.callStartTime
        ).toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
        });
        return (
          <div className="text-xs leading-relaxed text-[#374151]">
            <div>{formattedDate}</div>
            <div>{formattedTime}</div>
          </div>
        );
      },
    },
    {
      headerName: "Status",
      field: "callStatus",
      minWidth: 120,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const status = String(
          params.row?.callStatus || params.row?.callAction || ""
        ).toUpperCase();
        return status ? (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(
              status
            )}`}
          >
            {status}
          </span>
        ) : (
          <Empty />
        );
      },
    },
    {
      headerName: "Action",
      field: "callAction",
      minWidth: 110,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const action = String(params.row?.callAction || "").toUpperCase();
        return action ? (
          <span className="text-sm text-[#6B7280]">{action}</span>
        ) : (
          <Empty />
        );
      },
    },
    {
      headerName: "Recording",
      field: "recordingFileName",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <RecordingCell
          fileName={params.row.recordingFileName}
          recordingUrl={params.row.recordingUrl}
          storedRecordingUrl={params.row.storedRecordingUrl}
          callLogId={params.row._id || params.row.id}
          durationLabel={formatCallDuration(params.row)}
          recordingStatus={params.row.recordingStatus}
          recordingError={params.row.recordingError}
          recordingStoreStatus={params.row.recordingStoreStatus}
          recordingStoreError={params.row.recordingStoreError}
          autoPlay={
            !!focusCallId &&
            String(params.row._id || params.row.id) === String(focusCallId)
          }
        />
      ),
    },
  ];

  const tableDataShowHandler = async () => {
    try {
      setLoading(true);
      const response = await getAllCallLogs({
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search,
        status: statusFilter === "all" ? "" : statusFilter,
        dashboard: fromDashboard,
        period: filterQuery.period,
        from: filterQuery.from,
        to: filterQuery.to,
        teamIds: filterQuery.teamIds.join(","),
        agentIds: filterQuery.agentIds.join(","),
        projectIds: filterQuery.projectIds.join(","),
        sources: filterQuery.sources.join(","),
        stages: filterQuery.stages.join(","),
        focus: focusCallId,
      });
      const rows = (Array.isArray(response?.data) ? response.data : []).map(
        (item: any, index: number) => ({
          ...item,
          id: item._id || item.id || `row-${index}`,
        })
      );
      if (focusCallId) {
        rows.sort((a: { id?: string }, b: { id?: string }) => Number(String(b.id) === focusCallId) - Number(String(a.id) === focusCallId));
      }
      setCallData(rows);
      setMetaData(response?.metaData?.[0] ? response.metaData[0] : {});
      if (response?.stats) {
        setStats({
          total: response.stats.total || 0,
          answered: response.stats.answered || 0,
          missed: response.stats.missed || 0,
          initiated: response.stats.initiated || 0,
          withRecording: response.stats.withRecording || 0,
        });
      }
    } catch (err: any) {
      setCallData([]);
      setStats(emptyStats());
      console.error(err?.message || "Failed to load call logs", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTotal = metaData?.total || callData.length || 0;

  const setFilter = (value: string) => {
    setStatusFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {fromDashboard && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F3D5DE] bg-[#F9F0F4] px-4 py-3">
          <p className="text-sm font-semibold text-[#841C39]">
            Showing this dashboard’s calls for the selected dates and team.
          </p>
          <button
            type="button"
            className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#841C39]"
            onClick={() => history.push(urls.callLogs)}
          >
            Show every call
          </button>
        </div>
      )}
      <div className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Total calls"
          value={stats.total}
          accent="text-[#111827]"
          active={statusFilter === "all"}
          onClick={() => setFilter("all")}
        />
        <StatCard
          label="Answered"
          value={stats.answered}
          accent="text-emerald-700"
          active={statusFilter === "answered"}
          onClick={() => setFilter("answered")}
        />
        <StatCard
          label="Missed / failed"
          value={stats.missed}
          accent="text-red-600"
          active={statusFilter === "missed"}
          onClick={() => setFilter("missed")}
        />
        <StatCard
          label="Connecting"
          value={stats.initiated}
          accent="text-amber-700"
          active={statusFilter === "initiated"}
          onClick={() => setFilter("initiated")}
        />
        <StatCard
          label="With recording"
          value={stats.withRecording}
          accent="text-[#841C39]"
          active={statusFilter === "recording"}
          onClick={() => setFilter("recording")}
        />
      </div>

      <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full max-w-[420px]">
          <YhInput
            placeholder="Search phone, agent, status…"
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            autoFocus={isSearchFocused}
            startAdornment={<SearchSvg />}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </div>
        <div className="flex w-full items-center gap-2 sm:w-[220px]">
          <YhSelect
            placeholder="Filter status"
            className="!w-full"
            value={statusFilter}
            options={STATUS_FILTERS}
            onChange={(e) => setFilter(String(e.target.value || "all"))}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <YhTable
          columns={columns}
          rows={callData}
          getRowId={(row) => row.id || row._id}
          rowHeight={65}
          loading={loading}
          emptyMessage="No call logs found"
          className="h-full min-h-0 !rounded-none !border-0 !shadow-none"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={filteredTotal}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) => setPaginationModel({ pageSize: s, page: 0 })}
        />
      </div>
    </div>
  );
};

export default ShowAll;
